import React, { useEffect, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import { useSelector, useDispatch } from 'react-redux';
import { clearOrderNotification } from '../../slices/ecommerce/reducer';
import { useNavigate } from 'react-router-dom';

const OrderNotificationModal = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { newOrderNotification } = useSelector((state: any) => state.Ecommerce);
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        if (newOrderNotification) {
            setIsOpen(true);
        }
    }, [newOrderNotification]);

    const toggle = () => {
        if (isOpen) {
            dispatch(clearOrderNotification());
        }
        setIsOpen(!isOpen);
    };

    const handleRedirect = () => {
        const order_id = newOrderNotification?.order_id || newOrderNotification?.data?.order_id || newOrderNotification?.data?.id;
        const isSubscription = newOrderNotification?.type === 'NEW_SUBSCRIPTION';

        if (order_id) {
            if (isSubscription) {
                navigate(`/view-user-subscription/${order_id}`);
            } else {
                navigate(`/orders/view/${order_id}`);
            }
            dispatch(clearOrderNotification());
            setIsOpen(false);
        }
    };

    if (!newOrderNotification) return null;

    const isSubscription = newOrderNotification.type === 'NEW_SUBSCRIPTION';

    return (
        <Modal isOpen={isOpen} toggle={toggle} centered size={isSubscription ? "lg" : "md"}>
            <ModalHeader toggle={toggle} className={`bg-soft-${isSubscription ? 'primary' : 'info'} p-3`}>
                <i className={`${isSubscription ? 'ri-calendar-check-line' : 'ri-notification-3-line'} align-middle me-2`}></i>
                {newOrderNotification.title}
            </ModalHeader>
            <ModalBody className="p-0 overflow-hidden">
                <div className="p-4 text-center bg-light border-bottom position-relative">
                    <div className="position-absolute top-0 end-0 p-3">
                        <span className={`badge ${newOrderNotification.type == 'NEW_ORDER' ? 'bg-success' : newOrderNotification.type == 'NEW_SUBSCRIPTION' ? 'bg-primary' : 'bg-info'} shadow-sm`}>
                            {newOrderNotification.type == 'NEW_ORDER' ? 'NEW ORDER' : newOrderNotification.type == 'NEW_SUBSCRIPTION' ? 'NEW SUBSCRIPTION' : 'UPDATE'}
                        </span>
                    </div>
                    {newOrderNotification.data?.user?.profile || newOrderNotification.data?.users?.profile ? (
                        <div className="avatar-xl mx-auto mb-3 shadow-lg rounded-circle border border-4 border-white overflow-hidden bg-white">
                            <img
                                src={(newOrderNotification.data.user?.profile || newOrderNotification.data.users?.profile).startsWith('http')
                                    ? (newOrderNotification.data.user?.profile || newOrderNotification.data.users?.profile)
                                    : `${process.env.REACT_APP_API_URL?.replace("/v1", "")}/uploads/user/${newOrderNotification.data.user?.profile || newOrderNotification.data.users?.profile}`}
                                alt="User"
                                className="h-100 w-100 object-fit-cover"
                            />
                        </div>
                    ) : (
                        <div className="mx-auto mb-3" style={{ width: '100px', height: '100px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <img
                                src={`${process.env.REACT_APP_API_URL?.replace("/v1", "")}/uploads/logo/logo.png`}
                                alt="Logo"
                                className="img-fluid object-fit-contain"
                                onError={(e: any) => {
                                    e.target.onerror = null;
                                    e.target.src = "";
                                    e.target.parentElement.innerHTML = `<i class="ri-user-star-line text-primary display-5"></i>`;
                                }}
                            />
                        </div>
                    )}
                    <h5 className="mb-1 fw-bold text-dark">{newOrderNotification.title}</h5>
                    <div className="d-flex flex-wrap justify-content-center align-items-center gap-2 mb-0">
                        <p className="text-muted mb-0 small"><i className="ri-hashtag me-1"></i>{newOrderNotification.data?.user_subscription_order_id || newOrderNotification.data?.order_code_id || newOrderNotification.order_code_id || 'N/A'}</p>
                        
                        {!isSubscription && newOrderNotification.data?.orderStatus?.name && (
                            <span className="badge rounded-pill bg-light text-success border border-success-subtle px-2">
                                {newOrderNotification.data.orderStatus.name}
                            </span>
                        )}
                        
                        {!isSubscription && (newOrderNotification.data?.slot_type_name || newOrderNotification.data?.time_slot) && (
                            <span className="badge rounded-pill bg-soft-primary text-primary px-2">
                                <i className="ri-time-line me-1"></i>
                                {newOrderNotification.data.slot_type_name || newOrderNotification.data.time_slot}
                            </span>
                        )}

                        {isSubscription && newOrderNotification.data?.status_name && (
                            <span className="badge rounded-pill bg-light text-primary border border-primary-subtle px-2">
                                {newOrderNotification.data.status_name}
                            </span>
                        )}
                    </div>
                </div>

                <div className="p-4">
                    <div className="row g-4">
                        {/* Summary Section */}
                        <div className="col-12">
                            <div className={`p-3 bg-soft-${isSubscription ? 'success' : 'primary'} rounded-3 border border-dashed border-${isSubscription ? 'success' : 'primary'}`}>
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <p className="text-muted mb-1 small text-uppercase fw-semibold tracking-wider">Customer Name</p>
                                        <h6 className="mb-0 fs-16 fw-bold">
                                            {newOrderNotification.data?.user?.name || newOrderNotification.data?.users?.name || newOrderNotification.data?.customer_name || '-'}
                                        </h6>
                                    </div>
                                    <div className="text-end">
                                        <p className="text-muted mb-1 small text-uppercase fw-semibold tracking-wider">Total Amount</p>
                                        <h5 className="mb-0 text-success fw-bold">₹{newOrderNotification.data?.gross_amount || newOrderNotification.data?.total_amount || '0'}</h5>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Details Grid */}
                        <div className="col-6">
                            <div className="d-flex align-items-center">
                                <div className="avatar-xs flex-shrink-0 me-3">
                                    <div className="avatar-title bg-light text-primary rounded-circle"><i className="ri-phone-line"></i></div>
                                </div>
                                <div>
                                    <p className="text-muted mb-0 small">Phone Number</p>
                                    <h6 className="mb-0 fs-13">{newOrderNotification.data?.user?.mobile_number || newOrderNotification.data?.users?.mobile_number || newOrderNotification.data?.phone || 'N/A'}</h6>
                                </div>
                            </div>
                        </div>

                        <div className="col-6">
                            <div className="d-flex align-items-center">
                                <div className="avatar-xs flex-shrink-0 me-3">
                                    <div className="avatar-title bg-light text-primary rounded-circle"><i className="ri-calendar-event-line"></i></div>
                                </div>
                                <div>
                                    <p className="text-muted mb-0 small">{isSubscription ? 'Start Date' : 'Delivery Date'}</p>
                                    <h6 className="mb-0 fs-13">
                                        {newOrderNotification.data?.start_date || newOrderNotification.data?.delivery_date
                                            ? new Date(newOrderNotification.data?.start_date || newOrderNotification.data?.delivery_date).toLocaleString('en-IN', {
                                                day: '2-digit', month: '2-digit', year: 'numeric',
                                            }).replace(/\//g, '-')
                                            : 'N/A'}
                                    </h6>
                                </div>
                            </div>
                        </div>

                        {isSubscription && (
                            <div className="col-6">
                                <div className="d-flex align-items-center">
                                    <div className="avatar-xs flex-shrink-0 me-3">
                                        <div className="avatar-title bg-light text-warning rounded-circle"><i className="ri-calendar-todo-line"></i></div>
                                    </div>
                                    <div>
                                        <p className="text-muted mb-0 small">End Date</p>
                                        <h6 className="mb-0 fs-13">
                                            {newOrderNotification.data?.end_date
                                                ? new Date(newOrderNotification.data.end_date).toLocaleString('en-IN', {
                                                    day: '2-digit', month: '2-digit', year: 'numeric'
                                                }).replace(/\//g, '-')
                                                : 'N/A'}
                                        </h6>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="col-6">
                            <div className="d-flex align-items-center">
                                <div className="avatar-xs flex-shrink-0 me-3">
                                    <div className="avatar-title bg-light text-primary rounded-circle"><i className="ri-map-pin-2-line"></i></div>
                                </div>
                                <div>
                                    <p className="text-muted mb-0 small">Route / Area</p>
                                    <h6 className="mb-0 fs-13 text-truncate" style={{ maxWidth: '120px' }}>{newOrderNotification.data?.route?.name || newOrderNotification.data?.route_name || 'N/A'}</h6>
                                </div>
                            </div>
                        </div>

                        <div className="col-6">
                            <div className="d-flex align-items-center">
                                <div className="avatar-xs flex-shrink-0 me-3">
                                    <div className="avatar-title bg-light text-primary rounded-circle"><i className="ri-store-2-line"></i></div>
                                </div>
                                <div>
                                    <p className="text-muted mb-0 small">Store / Shop</p>
                                    <h6 className="mb-0 fs-13 text-truncate" style={{ maxWidth: '120px' }}>{newOrderNotification.data?.store?.name || newOrderNotification.data?.store_name || 'N/A'}</h6>
                                </div>
                            </div>
                        </div>

                        {/* Products Table */}
                        {(newOrderNotification.data?.orderItems || newOrderNotification.data?.details) && (
                            <div className="col-12 mt-4">
                                <h6 className="fs-13 mb-2 fw-bold text-dark"><i className="ri-shopping-bag-3-line me-1"></i>{isSubscription ? 'Subscription Items' : 'Ordered Items'}</h6>
                                <div className="table-responsive border rounded-3 bg-light-subtle">
                                    <table className="table table-sm table-borderless align-middle mb-0">
                                        <thead className="bg-light">
                                            <tr>
                                                <th className="ps-3 py-2 fs-11 text-muted text-uppercase">Item Name</th>
                                                <th className="text-center py-2 fs-11 text-muted text-uppercase">{isSubscription ? 'Validity' : 'Qty'}</th>
                                                <th className="text-end pe-3 py-2 fs-11 text-muted text-uppercase">Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(newOrderNotification.data.orderItems || newOrderNotification.data.details).map((item: any, idx: number) => (
                                                <tr key={idx} className="border-bottom-dashed">
                                                    <td className="ps-3 py-2">
                                                        <span className="fw-medium text-dark text-truncate d-inline-block" style={{ maxWidth: '200px' }}>
                                                            {item.product?.name || item.product_name || 'General Product'}
                                                        </span>
                                                        {isSubscription && item.variant?.name && (
                                                            <div className="small text-muted">{item.variant.name}</div>
                                                        )}
                                                    </td>
                                                    <td className="text-center py-2">
                                                        <span className="badge bg-light text-dark border">
                                                            {isSubscription ? `${item.validity} ${item.validity_type || 'Days'}` : `x${item.quantity}`}
                                                        </span>
                                                    </td>
                                                    <td className="text-end pe-3 py-2 fw-bold">₹{item.gross_amount || item.total_amount || item.price || '0'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </ModalBody>
            <ModalFooter className="justify-content-center">
                <Button color="light" onClick={toggle}>Close</Button>
                <Button color={isSubscription ? "success" : "primary"} onClick={handleRedirect}>
                    {isSubscription ? "View Subscription Details" : "View Order Details"}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default OrderNotificationModal;
